import { apiRequest } from '../../../shared/services/apiClient';
import { normalizeVisitPlace } from '../../field-operations/visitPlace';
import {
  AddConsultationFieldValueInput,
  AvailabilityQuery,
  AvailabilityResult,
  AppointmentType,
  AppointmentTypeListFilters,
  CALENDAR_AVAILABILITY_SETTING_KEYS,
  CalendarAvailabilitySettings,
  ChatBotEmployeeSelection,
  CHATBOT_EMPLOYEE_SELECTION_MODES,
  CalendarEvent,
  CalendarEventListFilters,
  CalendarEventStatusDefinition,
  CalendarEventStatusDefinitionListFilters,
  CalendarRecurrence,
  CreateCalendarEventStatusDefinitionInput,
  DayOfWeek,
  RecurringFrequency,
  Consultation,
  ConsultationFieldDefinition,
  ConsultationFieldDefinitionListFilters,
  ConsultationFieldValue,
  ConsultationListFilters,
  ConsultationTemplate,
  ConsultationTemplateField,
  ConsultationTemplateListFilters,
  CreateAppointmentTypeInput,
  CreateCalendarEventInput,
  CreateConsultationInput,
  CreateScheduleRuleInput,
  Employee,
  ScheduleRule,
  ScheduleRuleListFilters,
  UpdateCalendarEventStatusDefinitionInput,
  UpdateConsultationFieldValueInput,
  UpdateConsultationInput,
  UpdateCalendarEventInput,
  UpdateScheduleRuleInput,
} from '../types/Calendar';
import { normalizeCalendarEventStatusCode } from '../utils/calendarEventStatuses';

const pickItems = <T>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (Array.isArray(payload?.items)) return payload.items as T[];
  if (Array.isArray(payload?.data)) return payload.data as T[];
  if (Array.isArray(payload?.data?.items)) return payload.data.items as T[];
  return [];
};

const pickSingle = <T>(payload: any): T | null => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data as T;
  return payload as T;
};

const toQueryString = (params: Record<string, string | number | boolean | undefined>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const rendered = qs.toString();
  return rendered ? `?${rendered}` : '';
};

const normalizeConsultationFieldType = (value: any): ConsultationFieldDefinition['fieldType'] => {
  if (typeof value === 'string') {
    const normalized = value.trim();
    const lower = normalized.toLowerCase();
    if (lower === 'longtext') return 'longText';
    if (lower === 'datetime') return 'dateTime';
    if (lower === 'singleselect') return 'singleSelect';
    if (lower === 'multiselect') return 'multiSelect';
    if (lower === 'text' || lower === 'number' || lower === 'decimal' || lower === 'boolean' || lower === 'date' || lower === 'time') {
      return lower as ConsultationFieldDefinition['fieldType'];
    }
  }

  switch (Number(value)) {
    case 1:
      return 'longText';
    case 2:
      return 'number';
    case 3:
      return 'boolean';
    case 4:
      return 'date';
    case 5:
      return 'time';
    case 6:
      return 'dateTime';
    case 7:
      return 'singleSelect';
    case 8:
      return 'multiSelect';
    default:
      return 'text';
  }
};

const normalizeConsultationFieldOptions = (value: any): ConsultationFieldDefinition['options'] => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const options = value
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const id = typeof item.id === 'string' ? item.id.trim() : '';
      const label = typeof item.label === 'string' ? item.label.trim() : '';
      if (!id || !label) {
        return null;
      }

      return { id, label };
    })
    .filter((item): item is { id: string; label: string } => item !== null);

  return options.length > 0 ? options : undefined;
};

const normalizeDayOfWeek = (value: unknown): DayOfWeek | null => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    const allowed: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return allowed.includes(normalized as DayOfWeek) ? normalized as DayOfWeek : null;
  }

  if (typeof value === 'number' && value >= 0 && value <= 6) {
    const map: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return map[value] ?? null;
  }

  return null;
};

const normalizeRecurringFrequency = (value: unknown): RecurringFrequency => {
  const normalized = String(value ?? 'none').toLowerCase();
  const allowed: RecurringFrequency[] = ['none', 'daily', 'weekly', 'monthly', 'yearly'];
  return allowed.includes(normalized as RecurringFrequency) ? normalized as RecurringFrequency : 'none';
};

const normalizeRecurrence = (value: any): CalendarRecurrence | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const frequency = normalizeRecurringFrequency(value.frequency);
  if (frequency === 'none') {
    return undefined;
  }

  const daysOfWeek = Array.isArray(value.daysOfWeek)
    ? value.daysOfWeek.map(normalizeDayOfWeek).filter((item: DayOfWeek | null): item is DayOfWeek => Boolean(item))
    : [];

  return {
    frequency,
    interval: typeof value.interval === 'number' ? value.interval : 1,
    daysOfWeek,
    dayOfMonth: typeof value.dayOfMonth === 'number' ? value.dayOfMonth : undefined,
    endDate: value.endDate ? String(value.endDate).slice(0, 10) : undefined,
    maxOccurrences: typeof value.maxOccurrences === 'number' ? value.maxOccurrences : undefined,
  };
};

const normalizeCalendarEvent = (value: any): CalendarEvent => ({
  id: String(value?.id ?? ''),
  startAtUtc: String(value?.startAtUtc ?? ''),
  endAtUtc: String(value?.endAtUtc ?? ''),
  roomId: value?.roomId ? String(value.roomId) : undefined,
  staffId: value?.staffId ? String(value.staffId) : undefined,
  staffIds: Array.isArray(value?.staffIds) ? value.staffIds.map((item: any) => String(item)) : undefined,
  assetId: value?.assetId ? String(value.assetId) : undefined,
  assetIds: Array.isArray(value?.assetIds) ? value.assetIds.map((item: any) => String(item)) : undefined,
  contactId: value?.contactId ? String(value.contactId) : undefined,
  appointmentTypeId: value?.appointmentTypeId ? String(value.appointmentTypeId) : undefined,
  recurringPatternId: value?.recurringPatternId ? String(value.recurringPatternId) : undefined,
  recurringSeriesId: value?.recurringSeriesId ? String(value.recurringSeriesId) : undefined,
  recurrence: normalizeRecurrence(value?.recurrence),
  status: normalizeCalendarEventStatusCode(value?.status ? String(value.status) : 'confirmed'),
  title: value?.title ? String(value.title) : undefined,
  notes: value?.notes ? String(value.notes) : undefined,
  allowOverlap: Boolean(value?.allowOverlap),
  visitPlace: normalizeVisitPlace(value?.visitPlace),
  createdAtUtc: value?.createdAt ? String(value.createdAt) : value?.createdAtUtc ? String(value.createdAtUtc) : undefined,
  updatedAtUtc: value?.updatedAt ? String(value.updatedAt) : value?.updatedAtUtc ? String(value.updatedAtUtc) : undefined,
});

const normalizeConsultationCreationMode = (value: unknown): AppointmentType['consultationCreationMode'] => {
  const normalized = String(value ?? 'single').trim().toLowerCase();
  if (normalized === 'percontact') return 'perContact';
  if (normalized === 'perasset') return 'perAsset';
  if (normalized === 'peremployee') return 'perEmployee';
  return 'single';
};

const normalizeChatBotBookingFlow = (value: unknown): AppointmentType['chatBotBookingFlow'] => {
  const normalized = String(value ?? 'offerAvailableSlots').trim().toLowerCase();
  if (normalized === 'autonearestslot') return 'autoNearestSlot';
  return 'offerAvailableSlots';
};

const normalizeAppointmentType = (value: any): AppointmentType => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  defaultDurationMinutes: typeof value?.defaultDurationMinutes === 'number' ? value.defaultDurationMinutes : 0,
  color: value?.color ? String(value.color) : undefined,
  status: String(value?.status ?? 'active') as AppointmentType['status'],
  consultationCreationMode: normalizeConsultationCreationMode(value?.consultationCreationMode),
  chatBotBookingFlow: normalizeChatBotBookingFlow(value?.chatBotBookingFlow),
  chatBotInstruction: value?.chatBotInstruction ? String(value.chatBotInstruction) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeCalendarEventStatusDefinition = (value: any): CalendarEventStatusDefinition => ({
  id: String(value?.id ?? ''),
  code: normalizeCalendarEventStatusCode(value?.code ? String(value.code) : ''),
  name: String(value?.name ?? ''),
  color: value?.color ? String(value.color) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  status: String(value?.status ?? 'active').toLowerCase() === 'archived' ? 'archived' : 'active',
  isSystem: Boolean(value?.isSystem),
  isDefault: Boolean(value?.isDefault),
  countsAsBooking: Boolean(value?.countsAsBooking),
  suppressesReminders: Boolean(value?.suppressesReminders),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

/** A consideration counts as enabled unless the API explicitly reports it off. */
const normalizeChatBotEmployeeSelection = (value: unknown): ChatBotEmployeeSelection => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return CHATBOT_EMPLOYEE_SELECTION_MODES.includes(normalized as ChatBotEmployeeSelection)
    ? (normalized as ChatBotEmployeeSelection)
    : 'optional';
};

const normalizeAvailabilitySettings = (value: any): CalendarAvailabilitySettings => {
  const settings = CALENDAR_AVAILABILITY_SETTING_KEYS.reduce((accumulator, key) => {
    accumulator[key] = value?.[key] !== false;
    return accumulator;
  }, {} as CalendarAvailabilitySettings);

  settings.chatBotEmployeeSelection = normalizeChatBotEmployeeSelection(value?.chatBotEmployeeSelection);
  return settings;
};

const normalizeConsultation = (value: any): Consultation => ({
  id: String(value?.id ?? ''),
  eventId: String(value?.eventId ?? ''),
  assetId: String(value?.assetId ?? ''),
  contactId: value?.contactId ? String(value.contactId) : undefined,
  anamnesisSummary: value?.anamnesisSummary ? String(value.anamnesisSummary) : undefined,
  notes: value?.notes ? String(value.notes) : undefined,
  moduleKey: value?.moduleKey ? String(value.moduleKey) : undefined,
  domainProfileKey: value?.domainProfileKey ? String(value.domainProfileKey) : undefined,
  templateId: value?.templateId ? String(value.templateId) : undefined,
  status: value?.status ? String(value.status) : undefined,
  priority: value?.priority ? String(value.priority) : undefined,
  channel: value?.channel ? String(value.channel) : undefined,
  subjectType: value?.subjectType ? String(value.subjectType) : undefined,
  subjectId: value?.subjectId ? String(value.subjectId) : undefined,
  customerContactId: value?.customerContactId ? String(value.customerContactId) : undefined,
  assignedEmployeeId: value?.assignedEmployeeId ? String(value.assignedEmployeeId) : undefined,
  calendarEventId: value?.calendarEventId ? String(value.calendarEventId) : undefined,
  taskId: value?.taskId ? String(value.taskId) : undefined,
  conversationId: value?.conversationId ? String(value.conversationId) : undefined,
  externalReference: value?.externalReference ? String(value.externalReference) : undefined,
  scheduledAtUtc: value?.scheduledAtUtc ? String(value.scheduledAtUtc) : undefined,
  startedAtUtc: value?.startedAtUtc ? String(value.startedAtUtc) : undefined,
  completedAtUtc: value?.completedAtUtc ? String(value.completedAtUtc) : undefined,
  summary: value?.summary ? String(value.summary) : undefined,
  internalNotes: value?.internalNotes ? String(value.internalNotes) : undefined,
  outcomeType: value?.outcomeType ? String(value.outcomeType) : undefined,
  isDeleted: Boolean(value?.isDeleted),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeConsultationTemplateField = (value: any): ConsultationTemplateField => ({
  id: String(value?.id ?? ''),
  templateId: String(value?.templateId ?? ''),
  fieldDefinitionId: String(value?.fieldDefinitionId ?? ''),
  sectionKey: value?.sectionKey ? String(value.sectionKey) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  isRequired: Boolean(value?.isRequired),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeConsultationTemplate = (value: any): ConsultationTemplate => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  domainProfileKey: String(value?.domainProfileKey ?? ''),
  subjectType: value?.subjectType ? String(value.subjectType) : undefined,
  description: value?.description ? String(value.description) : undefined,
  defaultStatus: String(value?.defaultStatus ?? ''),
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  fields: Array.isArray(value?.fields) ? value.fields.map(normalizeConsultationTemplateField) : undefined,
});

const normalizeConsultationFieldDefinition = (value: any): ConsultationFieldDefinition => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  fieldType: normalizeConsultationFieldType(value?.fieldType),
  description: value?.description ? String(value.description) : undefined,
  isRequired: Boolean(value?.isRequired),
  defaultValue: value?.defaultValue ? String(value.defaultValue) : undefined,
  options: normalizeConsultationFieldOptions(value?.options),
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeConsultationFieldValue = (value: any): ConsultationFieldValue => ({
  id: String(value?.id ?? ''),
  consultationId: String(value?.consultationId ?? ''),
  fieldDefinitionId: String(value?.fieldDefinitionId ?? ''),
  value: value?.value !== undefined && value?.value !== null ? String(value.value) : undefined,
  sortOrder: typeof value?.sortOrder === 'number' ? value.sortOrder : 0,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

export const calendarService = {
  listEmployees: async (): Promise<Employee[]> => {
    const payload = await apiRequest<any>('/api/v1/employees');
    return pickItems<Employee>(payload);
  },

  getMyEmployee: async (): Promise<Employee | null> => {
    try {
      const payload = await apiRequest<any>('/api/v1/employees/me');
      return pickSingle<any>(payload)?.employee ?? null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  listScheduleRules: async (filters: ScheduleRuleListFilters = {}): Promise<ScheduleRule[]> => {
    const query = toQueryString({
      type: filters.type,
      chatBotBlockoutId: filters.chatBotBlockoutId,
      staffId: filters.staffId,
      roomId: filters.roomId,
      isEnabled: filters.isEnabled,
    });
    const payload = await apiRequest<any>(`/api/v1/calendar/schedule-rules${query}`);
    return pickItems<ScheduleRule>(payload);
  },

  getScheduleRuleById: async (ruleId: string): Promise<ScheduleRule | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/calendar/schedule-rules/${ruleId}`);
      return pickSingle<ScheduleRule>(payload);
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createScheduleRule: async (input: CreateScheduleRuleInput): Promise<ScheduleRule | null> => {
    const payload = await apiRequest<any>('/api/v1/calendar/schedule-rules', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return pickSingle<ScheduleRule>(payload);
  },

  updateScheduleRule: async (ruleId: string, input: UpdateScheduleRuleInput): Promise<ScheduleRule | null> => {
    const payload = await apiRequest<any>(`/api/v1/calendar/schedule-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return pickSingle<ScheduleRule>(payload);
  },

  deleteScheduleRule: async (ruleId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/calendar/schedule-rules/${ruleId}`, {
      method: 'DELETE',
    });
  },

  listEvents: async (filters: CalendarEventListFilters = {}): Promise<CalendarEvent[]> => {
    const query = toQueryString({
      fromUtc: filters.fromUtc,
      toUtc: filters.toUtc,
      roomId: filters.roomId,
      staffId: filters.staffId,
      status: filters.status,
    });
    const payload = await apiRequest<any>(`/api/v1/calendar/events${query}`);
    return pickItems<any>(payload).map(normalizeCalendarEvent);
  },

  getEventById: async (eventId: string): Promise<CalendarEvent | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/calendar/events/${eventId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeCalendarEvent(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createEvent: async (input: CreateCalendarEventInput): Promise<CalendarEvent | null> => {
    const payload = await apiRequest<any>('/api/v1/calendar/events', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeCalendarEvent(item) : null;
  },

  updateEvent: async (eventId: string, input: UpdateCalendarEventInput): Promise<CalendarEvent | null> => {
    const payload = await apiRequest<any>(`/api/v1/calendar/events/${eventId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeCalendarEvent(item) : null;
  },

  deleteEvent: async (eventId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/calendar/events/${eventId}`, {
      method: 'DELETE',
    });
  },

  getAvailability: async (query: AvailabilityQuery): Promise<AvailabilityResult> => {
    const rendered = toQueryString({
      fromUtc: query.fromUtc,
      ...(query.toUtc ? { toUtc: query.toUtc } : {}),
      slotMinutes: query.slotMinutes,
      durationMinutes: query.durationMinutes,
      roomId: query.roomId,
      staffId: query.staffId,
      appointmentTypeId: query.appointmentTypeId,
      bookingChannel: query.bookingChannel,
      page: query.page,
      pageSize: query.pageSize,
    });
    const payload = await apiRequest<any>(`/api/v1/calendar/availability${rendered}`);

    const item = pickSingle<any>(payload);
    const slots = Array.isArray(item?.slots) ? item.slots.map((slot: any) => ({
      startAtUtc: String(slot?.startAtUtc ?? ''),
      endAtUtc: String(slot?.endAtUtc ?? ''),
      roomId: slot?.roomId ? String(slot.roomId) : undefined,
      staffId: slot?.staffId ? String(slot.staffId) : undefined,
    })) : [];

    if (item) {
      return {
        fromUtc: String(item.fromUtc ?? query.fromUtc),
        toUtc: query.toUtc ? String(item.toUtc ?? query.toUtc) : undefined,
        slotMinutes: Number(item.slotMinutes ?? query.slotMinutes ?? 20),
        durationMinutes: Number(item.durationMinutes ?? query.durationMinutes ?? query.slotMinutes ?? 20),
        roomId: item.roomId ? String(item.roomId) : query.roomId,
        staffId: item.staffId ? String(item.staffId) : query.staffId,
        page: Number(item.page ?? query.page ?? 1),
        pageSize: Number(item.pageSize ?? query.pageSize ?? 100),
        hasNext: Boolean(item.hasNext),
        hasPrevious: Boolean(item.hasPrevious),
        slots,
      };
    }

    return {
      fromUtc: query.fromUtc,
      toUtc: query.toUtc || undefined,
      slotMinutes: query.slotMinutes || 20,
      durationMinutes: query.durationMinutes || query.slotMinutes || 20,
      roomId: query.roomId,
      staffId: query.staffId,
      page: query.page || 1,
      pageSize: query.pageSize || 100,
      hasNext: false,
      hasPrevious: false,
      slots: [],
    };
  },

  getAvailabilitySettings: async (): Promise<CalendarAvailabilitySettings> => {
    const payload = await apiRequest<any>('/api/v1/calendar/availability-settings');
    return normalizeAvailabilitySettings(pickSingle<any>(payload));
  },

  updateAvailabilitySettings: async (
    changes: Partial<CalendarAvailabilitySettings>,
  ): Promise<CalendarAvailabilitySettings> => {
    const payload = await apiRequest<any>('/api/v1/calendar/availability-settings', {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
    return normalizeAvailabilitySettings(pickSingle<any>(payload));
  },

  listEventStatuses: async (
    filters: CalendarEventStatusDefinitionListFilters = {},
  ): Promise<CalendarEventStatusDefinition[]> => {
    const qs = toQueryString({
      status: filters.status,
    });
    const payload = await apiRequest<any>(`/api/v1/calendar/event-statuses${qs}`);
    return pickItems<any>(payload)
      .map(normalizeCalendarEventStatusDefinition)
      .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
  },

  createEventStatus: async (
    input: CreateCalendarEventStatusDefinitionInput,
  ): Promise<CalendarEventStatusDefinition | null> => {
    const payload = await apiRequest<any>('/api/v1/calendar/event-statuses', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeCalendarEventStatusDefinition(item) : null;
  },

  updateEventStatus: async (
    statusId: string,
    input: UpdateCalendarEventStatusDefinitionInput,
  ): Promise<CalendarEventStatusDefinition | null> => {
    const payload = await apiRequest<any>(`/api/v1/calendar/event-statuses/${statusId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeCalendarEventStatusDefinition(item) : null;
  },

  listAppointmentTypes: async (filters: AppointmentTypeListFilters = {}): Promise<AppointmentType[]> => {
    const query = toQueryString({
      status: filters.status,
    });
    const payload = await apiRequest<any>(`/api/v1/calendar/appointment-types${query}`);
    return pickItems<any>(payload).map(normalizeAppointmentType);
  },

  createAppointmentType: async (input: CreateAppointmentTypeInput): Promise<AppointmentType | null> => {
    const payload = await apiRequest<any>('/api/v1/calendar/appointment-types', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeAppointmentType(item) : null;
  },

  listConsultations: async (filters: ConsultationListFilters = {}): Promise<Consultation[]> => {
    const query = toQueryString({
      eventId: filters.eventId,
      assetId: filters.assetId,
      contactId: filters.contactId,
      includeDeleted: filters.includeDeleted,
    });
    const payload = await apiRequest<any>(`/api/v1/consultations${query}`);
    return pickItems<any>(payload).map(normalizeConsultation);
  },

  getConsultationById: async (consultationId: string): Promise<Consultation | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeConsultation(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  createConsultation: async (input: CreateConsultationInput): Promise<Consultation | null> => {
    const payload = await apiRequest<any>('/api/v1/consultations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  updateConsultation: async (consultationId: string, input: UpdateConsultationInput): Promise<Consultation | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultation(item) : null;
  },

  listConsultationFieldDefinitions: async (filters: ConsultationFieldDefinitionListFilters = {}): Promise<ConsultationFieldDefinition[]> => {
    const query = toQueryString({
      fieldType: filters.fieldType,
      isActive: filters.isActive,
    });
    const payload = await apiRequest<any>(`/api/v1/consultations/field-definitions${query}`);
    return pickItems<any>(payload).map(normalizeConsultationFieldDefinition);
  },

  listConsultationTemplates: async (filters: ConsultationTemplateListFilters = {}): Promise<ConsultationTemplate[]> => {
    const query = toQueryString({
      domainProfileKey: filters.domainProfileKey,
      subjectType: filters.subjectType,
      isActive: filters.isActive,
      search: filters.search,
    });
    const payload = await apiRequest<any>(`/api/v1/consultation-templates${query}`);
    return pickItems<any>(payload).map(normalizeConsultationTemplate);
  },

  getConsultationTemplateById: async (templateId: string): Promise<ConsultationTemplate | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/consultation-templates/${templateId}`);
      const item = pickSingle<any>(payload);
      return item ? normalizeConsultationTemplate(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) return null;
      throw error;
    }
  },

  listConsultationFieldValues: async (consultationId: string): Promise<ConsultationFieldValue[]> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values`);
    return pickItems<any>(payload).map(normalizeConsultationFieldValue);
  },

  addConsultationFieldValue: async (consultationId: string, input: AddConsultationFieldValueInput): Promise<ConsultationFieldValue | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldValue(item) : null;
  },

  updateConsultationFieldValue: async (consultationId: string, fieldDefinitionId: string, input: UpdateConsultationFieldValueInput): Promise<ConsultationFieldValue | null> => {
    const payload = await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    const item = pickSingle<any>(payload);
    return item ? normalizeConsultationFieldValue(item) : null;
  },

  removeConsultationFieldValue: async (consultationId: string, fieldDefinitionId: string): Promise<void> => {
    await apiRequest<any>(`/api/v1/consultations/${consultationId}/field-values/${fieldDefinitionId}`, {
      method: 'DELETE',
    });
  },
};

export const {
  listEmployees,
  getMyEmployee,
  listScheduleRules,
  getScheduleRuleById,
  createScheduleRule,
  updateScheduleRule,
  deleteScheduleRule,
  listEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  getAvailability,
  getAvailabilitySettings,
  updateAvailabilitySettings,
  listAppointmentTypes,
  createAppointmentType,
  listEventStatuses,
  createEventStatus,
  updateEventStatus,
  listConsultations,
  getConsultationById,
  createConsultation,
  updateConsultation,
  listConsultationFieldDefinitions,
  listConsultationTemplates,
  getConsultationTemplateById,
  listConsultationFieldValues,
  addConsultationFieldValue,
  updateConsultationFieldValue,
  removeConsultationFieldValue,
} = calendarService;
